import type { LocaleKey } from "@caspeco/casper-ui-library/dist/components/date-picker/types";
import i18n from "i18next";

/**
 * Looks up a key in a multilingual object with case-insensitive matching.
 * Returns the value if non-empty, or undefined if not found or the value is empty.
 */
function findLocalizedEntry(
    multiLangObj: { [key: string]: string },
    key: string
): string | undefined {
    if (multiLangObj[key]) return multiLangObj[key];
    const keyLower = key.toLowerCase();
    const matchingKey = Object.keys(multiLangObj).find((k) => k.toLowerCase() === keyLower);
    const value = matchingKey !== undefined ? multiLangObj[matchingKey] : undefined;
    return value || undefined;
}

/**
 * Finds the first key in a multilingual object whose language prefix matches the given base
 * language (case-insensitive). Used to match bare language codes (e.g. "da") against full
 * locale keys (e.g. "da-dk" or "da-DK") when the exact key is absent.
 */
function findLocalizedEntryByPrefix(
    multiLangObj: { [key: string]: string },
    baseLang: string
): string | undefined {
    const prefixLower = baseLang.toLowerCase() + "-";
    const matchingKey = Object.keys(multiLangObj).find((k) =>
        k.toLowerCase().startsWith(prefixLower)
    );
    const value = matchingKey !== undefined ? multiLangObj[matchingKey] : undefined;
    return value || undefined;
}

/**
 * Extracts the localized string from a multilingual object based on the current language.
 *
 * Keys in the multilingual object are expected to follow BCP 47 format (e.g. "sv-SE", "en-GB").
 * Matching is case-insensitive to handle edge cases where the key or language code may not
 * be correctly cased.
 *
 * Lookup priority:
 * 1. Current language exact match (e.g. "da" or "da-DK")
 * 2. Base language exact match (e.g. "da" from "da-DK")
 * 3. Any key prefixed with base language (e.g. "da-dk" matched by "da")
 * 4. Fallback language exact match (e.g. "sv-SE")
 * 5. Fallback base language exact match (e.g. "sv" from "sv-SE")
 * 6. Any key prefixed with fallback base language (e.g. "sv-se" matched by "sv")
 * 7. First available value
 *
 * @param multiLangObj - Object with BCP 47 language codes as keys and translations as values
 * @param fallbackLang - Fallback language if current language not found (defaults to 'sv-SE')
 * @returns Localized string or empty string if not found
 * @example
 * const name = getLocalizedValue({ "sv-SE": "Festmeny", "en-GB": "Party Menu" })
 */
function getLocalizedValue(
    multiLangObj: { [key: string]: string } | undefined,
    fallbackLang: string = "sv-SE"
): string {
    if (!multiLangObj) return "";

    const currentLang = i18n.language;
    const baseLang = currentLang.split("-")[0];
    const fallbackBaseLang = fallbackLang.split("-")[0];

    // 1. Current language exact match (e.g. "da" or "da-DK")
    const current = findLocalizedEntry(multiLangObj, currentLang);
    if (current) return current;

    // 2. Base language exact match (e.g. "da" from "da-DK") — only when currentLang has a region
    if (baseLang !== currentLang) {
        const base = findLocalizedEntry(multiLangObj, baseLang);
        if (base) return base;
    }

    // 3. Any key prefixed with base language (e.g. "da-dk" matched by currentLang "da")
    const basePrefix = findLocalizedEntryByPrefix(multiLangObj, baseLang);
    if (basePrefix) return basePrefix;

    // 4. Fallback language exact match (e.g. "sv-SE")
    const fallback = findLocalizedEntry(multiLangObj, fallbackLang);
    if (fallback) return fallback;

    // 5. Fallback base language exact match (e.g. "sv" from "sv-SE")
    if (fallbackBaseLang !== fallbackLang) {
        const fallbackBase = findLocalizedEntry(multiLangObj, fallbackBaseLang);
        if (fallbackBase) return fallbackBase;
    }

    // 6. Any key prefixed with fallback base language (e.g. "sv-se" matched by "sv")
    const fallbackPrefix = findLocalizedEntryByPrefix(multiLangObj, fallbackBaseLang);
    if (fallbackPrefix) return fallbackPrefix;

    // 7. First available value
    return Object.values(multiLangObj)[0] ?? "";
}

enum SupportedLanguage {
    EN = "en",
    SV = "sv",
    NB = "nb",
    DA = "da",
    FI = "fi",
    ES = "es",
    NL = "nl",
    NL_BE = "nl-BE",
    FR = "fr",
    FR_BE = "fr-BE",
    FR_FR = "fr-FR",
    ET = "et",
    LT = "lt",
    LV = "lv",
    DE = "de",
    DE_BE = "de-BE",
}

export enum SupportedLocale {
    SV = "sv-SE",
    EN = "en-GB",
    NB = "nb-NO",
    DA = "da-DK",
    FI = "fi-FI",
    ES = "es-ES",
    NL = "nl-NL",
    NL_BE = "nl-BE",
    FR = "fr-FR",
    FR_BE = "fr-BE",
    ET = "et-EE",
    LT = "lt-LT",
    LV = "lv-LV",
    DE = "de-DE",
    DE_BE = "de-BE",
}

const langFromLocale = (lang: string): string => lang.split("-")[0].toLowerCase();

const LOCALE_BY_LANGUAGE: Record<SupportedLanguage, SupportedLocale> = {
    [SupportedLanguage.EN]: SupportedLocale.EN,
    [SupportedLanguage.SV]: SupportedLocale.SV,
    [SupportedLanguage.NB]: SupportedLocale.NB,
    [SupportedLanguage.DA]: SupportedLocale.DA,
    [SupportedLanguage.FI]: SupportedLocale.FI,
    [SupportedLanguage.ES]: SupportedLocale.ES,
    [SupportedLanguage.NL]: SupportedLocale.NL,
    [SupportedLanguage.NL_BE]: SupportedLocale.NL_BE,
    [SupportedLanguage.FR]: SupportedLocale.FR,
    [SupportedLanguage.FR_BE]: SupportedLocale.FR_BE,
    [SupportedLanguage.FR_FR]: SupportedLocale.FR,
    [SupportedLanguage.ET]: SupportedLocale.ET,
    [SupportedLanguage.LT]: SupportedLocale.LT,
    [SupportedLanguage.LV]: SupportedLocale.LV,
    [SupportedLanguage.DE]: SupportedLocale.DE,
    [SupportedLanguage.DE_BE]: SupportedLocale.DE_BE,
};

/**
 * Will return the locale based on the language.
 *
 * The full language tag is matched before its base language, so region variants survive: "de-BE"
 * stays Belgian German rather than collapsing to "de-DE". The locale returned here is sent to the
 * API as a booking's `lang` and decides which language the guest's confirmation is written in, so
 * collapsing it would email a Belgian guest in the wrong regional variant.
 */
export const getLocaleFromLang = (locale: string): SupportedLocale => {
    const exact = LOCALE_BY_LANGUAGE[locale as SupportedLanguage];
    if (exact) return exact;

    const lang = langFromLocale(locale) as SupportedLanguage;
    return LOCALE_BY_LANGUAGE[lang] ?? SupportedLocale.EN;
};

/**
 * Narrows a locale to one the shared DatePicker ships month and day names for.
 *
 * `casper-ui-library`'s `LocaleKey` has no German entry, so a German guest gets an English calendar
 * rather than an unsupported locale key. Drop this fallback once the library covers de-DE/de-BE.
 */
export const getDatePickerLocale = (locale: SupportedLocale): LocaleKey => {
    if (locale === SupportedLocale.DE || locale === SupportedLocale.DE_BE)
        return SupportedLocale.EN;
    return locale;
};

const NAME_KEY_BY_LANGUAGE: Record<SupportedLanguage, string> = {
    [SupportedLanguage.EN]: "miscEnglish",
    [SupportedLanguage.SV]: "miscSwedish",
    [SupportedLanguage.NB]: "miscNorwegian",
    [SupportedLanguage.DA]: "miscDanish",
    [SupportedLanguage.FI]: "miscFinnish",
    [SupportedLanguage.ES]: "miscEspanol",
    [SupportedLanguage.NL]: "miscDutch",
    [SupportedLanguage.NL_BE]: "miscDutchBelgium",
    [SupportedLanguage.FR]: "miscFrench",
    [SupportedLanguage.FR_BE]: "miscFrenchBelgium",
    [SupportedLanguage.FR_FR]: "miscFrench",
    [SupportedLanguage.ET]: "miscEstonian",
    [SupportedLanguage.LT]: "miscLithuanian",
    [SupportedLanguage.LV]: "miscLatvian",
    [SupportedLanguage.DE]: "miscGerman",
    [SupportedLanguage.DE_BE]: "miscGermanBelgium",
};

/**
 * Helper to get translated name of current used i18n language.
 * @returns translated name of current language.
 * For ex. i18n language is "SV" the return value will be "Svenska".
 *
 * Matches the full language tag before its base language, so a Belgian variant keeps its own name
 * ("Nederlands (België)") instead of falling back to the plain language.
 */
export const getNameOfCurrentLang = () => {
    const exact = NAME_KEY_BY_LANGUAGE[i18n.language as SupportedLanguage];
    if (exact) return i18n.t(exact);

    const lang = langFromLocale(i18n.language) as SupportedLanguage;
    return i18n.t(NAME_KEY_BY_LANGUAGE[lang] ?? "miscEnglish");
};

export { getLocalizedValue };
